/**
 * Golang-style functions for TypeScript
 */

/**
 * Creates a new slice (array) with the specified length and optional capacity.
 * @param len Length of the new slice
 * @param cap Optional capacity of the slice
 * @returns A new array with the specified length
 */
/* eslint-disable */

export function make<T>(
	lenOrIterableOrArrayLike: int | Iterable<T> | ArrayLike<T>,
	mapFnOrCap?: ((element: any, index: int) => T) | int
): T[]
export function make<K, V>(entries: [K, V][]): Map<K, V>
export function make<K, V>(mapType: { new (): Map<K, V> }): Map<K, V>
export function make<K, V>(
	mapType: { new (): Map<K, V> },
	entries: [K, V][] | Iterable<[K, V]>
): Map<K, V>
export function make<T>(setType: { new (): Set<T> }): Set<T>
export function make<T>(setType: { new (): Set<T> }, values: T[] | Iterable<T>): Set<T>
export function make<T, K, V>(
	arg1: int | Iterable<T> | ArrayLike<T> | { new (): Map<K, V> } | { new (): Set<T> },
	arg2?: ((element: any, index: int) => T) | int | [K, V][] | Iterable<[K, V]> | T[] | Iterable<T>
): T[] | Map<K, V> | Set<T> {
	// Case 1: Called with a length and optional capacity (array creation)
	if (typeof arg1 === "number") {
		const len = arg1
		const cap = typeof arg2 === "number" ? arg2 : undefined
		if (!isNil(cap) && cap < len) {
			log.Fatal("Make: capacity must be greater than or equal to length")
		}
		return new Array<T>(len)
	}

	// Case 3: Called with Map constructor
	if (arg1 === Map) {
		if (arg2) {
			// With entries
			const entries = arg2 as Iterable<[K, V]>
			return new Map<K, V>(entries)
		}
		// Without entries
		return new Map<K, V>()
	}

	// Case 5: Called with Set constructor
	if (arg1 === Set) {
		if (arg2) {
			// With values
			const values = arg2 as Iterable<T>
			return new Set<T>(values)
		}
		// Without values
		return new Set<T>()
	}

	// Case 2: Called with an iterable/array-like and optional mapping function
	const iterableOrArrayLike = arg1 as Iterable<T> | ArrayLike<T>
	const mapFn = typeof arg2 === "function" ? (arg2 as (element: any, index: int) => T) : undefined

	if (mapFn) {
		return Array.from(iterableOrArrayLike, mapFn)
	}

	// Case 4: Called with entries array to create a Map
	if (Array.isArray(arg1) && arg1.length > 0 && Array.isArray(arg1[0]) && arg1[0].length === 2) {
		return new Map(arg1 as [K, V][])
	}

	return Array.from(iterableOrArrayLike as any)
}

/**
 * Appends elements to the end of a slice and returns the result.
 * @param slice The original slice
 * @param items Items to append
 * @returns The resulting slice after appending items
 */
export function append<T>(slice: T[], ...items: T[]): T[] {
	return [...slice, ...items]
}

/**
 * Copies elements from a source slice to a destination slice.
 * @param dst Destination slice
 * @param src Source slice
 * @returns Number of elements copied
 */
export function copy<T>(dst: T[], src: T[]): int {
	const n = Math.min(dst.length, src.length)
	for (let i = 0; i < n; i++) {
		dst[i] = src[i]
	}
	return n
}

/**
 * Returns the length of a collection.
 * @param collection A collection (array, string, Map, or Set)
 * @returns The length of the collection
 */
export function len<T>(collection: T[] | Map<any, any> | Set<any> | string): int {
	if (typeof collection === "string" || Array.isArray(collection)) {
		return collection.length
	} else if (collection instanceof Map || collection instanceof Set) {
		return collection.size
	}
	return 0
}

export function isNil(v: unknown): v is nil {
	return v === null || v === undefined
}

const gt = globalThis as any

Object.assign(gt, {
	make,
	append,
	copy,
	len,
	isNil,
})
